import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { VersionMessage } from "../../interfaces";

interface PreferencesSlice {
  theme: string | null;
  origLogo: boolean;
  isDarkMode: boolean;
  logoutTime: number;
  showNotesOnSummary: boolean;
  uppercase: boolean;
  showGDPRModal: boolean;
  versionMessage: VersionMessage | null;
  pathNames: Record<string, string>;
  customThemeUrl?: string;
}

const initialState: PreferencesSlice = {
  theme: localStorage.getItem("theme"),
  customThemeUrl: "",
  origLogo: true,
  isDarkMode: false,
  logoutTime: 15 * 60 * 1000,
  showNotesOnSummary: localStorage.getItem("showNotes") === "true",
  uppercase: false,
  showGDPRModal: false,
  versionMessage: null,
  pathNames: {},
};

export const preferencesSlice = createSlice({
  name: "preferencesSlice",
  initialState,
  reducers: {
    setPreferences: (
      state,
      action: PayloadAction<{
        theme?: string;
        logoutTime?: string;
        uppercase?: boolean;
        customThemeUrl?: string;
      }>,
    ) => {
      const { theme, logoutTime, uppercase } = action.payload;
      if (theme) {
        let isDarkMode = false;
        if (theme === "auto") {
          const currHour = dayjs().hour();
          isDarkMode = !(currHour >= 6 && currHour < 21);
        } else {
          isDarkMode = theme === "night";
        }
        state.isDarkMode = isDarkMode;
      }

      if (logoutTime) {
        const timeInMs = Number(logoutTime) * 60 * 1000;
        state.logoutTime = timeInMs;
        localStorage.setItem("logoutTime", logoutTime);
      }

      if (typeof uppercase === "boolean") {
        state.uppercase = uppercase;
      }
    },
    setPathNames: (state, action: PayloadAction<Record<string, string>>) => {
      state.pathNames = action.payload;
    },

    setShowNotesOnSummary: (state, action) => {
      state.showNotesOnSummary = action.payload;
    },
    setShowGDPRModal: (state, action) => {
      state.showGDPRModal = action.payload;
    },
    setTheme: (state, action) => {
      state.theme = action.payload.theme;
      state.customThemeUrl = action.payload.customThemeUrl;
      state.origLogo = action.payload.origLogo;
      localStorage.setItem("theme", action.payload.theme);
    },
    setVersionMessage: (
      state,
      action: PayloadAction<VersionMessage | null>,
    ) => {
      state.versionMessage = action.payload;
    },
  },
});

export const {
  setTheme,
  setPreferences,
  setShowNotesOnSummary,
  setShowGDPRModal,
  setVersionMessage,
  setPathNames,
} = preferencesSlice.actions;

export default preferencesSlice.reducer;
